/**
 * Investor Detail Layout — shared header + tab navigation
 *
 * Route: /admin/reporting/investors/:id (layout)
 */

import { Outlet, Link, useMatchRoute } from '@tanstack/react-router'
import { Stack } from '@/common/components/stack'
import { Text } from '@/common/components/text'
import { LoadingSpinner } from '@/common/components/loading-spinner'
import { useInvestorProfile } from '@/features/reporting/hooks/useReportingData'
import * as styles from '../index.css'

export const Route = createFileRoute({
  component: InvestorDetailLayout,
})

function formatRelativeTime(dateStr: string | null): string {
  if (!dateStr) return 'Never'
  const date = new Date(dateStr)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

  if (diffDays === 0) return 'Today'
  if (diffDays === 1) return 'Yesterday'
  if (diffDays < 7) return `${diffDays} days ago`
  if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`
  if (diffDays < 365) return `${Math.floor(diffDays / 30)} months ago`
  return `${Math.floor(diffDays / 365)} years ago`
}

const TAB_STYLE: React.CSSProperties = {
  padding: '8px 16px',
  textDecoration: 'none',
  fontSize: '14px',
  fontWeight: 500,
  borderBottom: '2px solid transparent',
  color: '#666',
}

const TAB_ACTIVE_STYLE: React.CSSProperties = {
  ...TAB_STYLE,
  color: '#027F87',
  borderBottomColor: '#027F87',
}

function InvestorDetailLayout() {
  const { id } = Route.useParams()
  const matchRoute = useMatchRoute()
  const { data: profile, isLoading } = useInvestorProfile(id)

  const isMetricsTab = matchRoute({ to: '/admin/reporting/investors/$id', params: { id } })
  const isActivityTab = matchRoute({ to: '/admin/reporting/investors/$id/activity', params: { id } })
  const isAgreementTab = matchRoute({ to: '/admin/reporting/investors/$id/agreement', params: { id } })

  if (isLoading) {
    return (
      <div className={styles.container}>
        <div className={styles.content}>
          <Stack gap="16" align="center" justify="center" style={{ minHeight: '400px' }}>
            <LoadingSpinner size="md" />
          </Stack>
        </div>
      </div>
    )
  }

  if (!profile) {
    return (
      <div className={styles.container}>
        <div className={styles.content}>
          <Link to="/admin/reporting/investors" search={{ tab: 'individual', sortBy: 'last_login', sortDir: 'desc' }} style={{ textDecoration: 'none', color: '#027F87', fontSize: '14px' }}>
            &larr; Back to Investors
          </Link>
          <Stack gap="16" align="center" justify="center" style={{ minHeight: '400px' }}>
            <Text variant="body" color="muted">Investor not found.</Text>
          </Stack>
        </div>
      </div>
    )
  }

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        {/* Back Link */}
        <Link to="/admin/reporting/investors" search={{ tab: 'individual', sortBy: 'last_login', sortDir: 'desc' }} style={{ textDecoration: 'none', color: '#027F87', fontSize: '14px' }}>
          &larr; Back to Investors
        </Link>

        {/* Profile Header */}
        <div style={{
          backgroundColor: '#fff',
          borderRadius: '8px',
          padding: '24px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
        }}>
          <Stack gap="8">
            <Text as="h1" variant="h4" weight="bold">
              {profile.first_name} {profile.last_name}
            </Text>
            <Stack direction="row" gap="24" style={{ flexWrap: 'wrap' }}>
              {profile.email && (
                <Text variant="bodySm" color="muted">{profile.email}</Text>
              )}
              {profile.company_name && (
                <Text variant="bodySm" color="muted">{profile.company_name}</Text>
              )}
              {profile.markets && profile.markets.length > 0 && (
                <Text variant="bodySm" color="muted">
                  Markets: {profile.markets.join(', ')}
                </Text>
              )}
              <Text variant="bodySm" color="muted">
                Last Login: {formatRelativeTime(profile.last_login_at)}
              </Text>
              {profile.signup_date && (
                <Text variant="bodySm" color="muted">
                  Signed Up: {new Date(profile.signup_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                </Text>
              )}
            </Stack>
          </Stack>
        </div>

        {/* Tab Navigation */}
        <div style={{ display: 'flex', gap: '0', borderBottom: '1px solid #e0e0e0' }}>
          <Link
            to="/admin/reporting/investors/$id"
            params={{ id }}
            style={isMetricsTab ? TAB_ACTIVE_STYLE : TAB_STYLE}
          >
            Metrics
          </Link>
          <Link
            to="/admin/reporting/investors/$id/activity"
            params={{ id }}
            style={isActivityTab ? TAB_ACTIVE_STYLE : TAB_STYLE}
          >
            Activity Log
          </Link>
          <Link
            to="/admin/reporting/investors/$id/agreement"
            params={{ id }}
            style={isAgreementTab ? TAB_ACTIVE_STYLE : TAB_STYLE}
          >
            Agreement
          </Link>
        </div>

        {/* Child route content */}
        <Outlet />
      </div>
    </div>
  )
}
